import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Plus, Trash2, ChevronUp, ChevronDown, Clock, AlertTriangle, Check } from 'lucide-react';
import { botAPI } from '../services/api';

export default function PriceAlerts() {
    const [alerts, setAlerts] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [showTriggered, setShowTriggered] = useState(false);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ symbol: '', condition: 'above', targetPrice: '', note: '' });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const loadAlerts = useCallback(async () => {
        try {
            const res = await botAPI.getAlerts(showTriggered);
            setAlerts(res.data);
        } catch (err) {
            console.error('Failed to load alerts');
        }
        setLoading(false);
    }, [showTriggered]);

    useEffect(() => { loadAlerts(); }, [loadAlerts]);

    const createAlert = async (e) => {
        e.preventDefault();
        setError(null);
        if (!form.symbol || !form.targetPrice) {
            setError('Symbol and target price required');
            return;
        }
        try {
            await botAPI.createAlert(form);
            setForm({ symbol: '', condition: 'above', targetPrice: '', note: '' });
            setShowForm(false);
            setSuccess('Alert created!');
            setTimeout(() => setSuccess(null), 3000);
            loadAlerts();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create alert');
        }
    };

    const deleteAlert = async (id) => {
        try {
            await botAPI.deleteAlert(id);
            setAlerts(prev => prev.filter(a => a._id !== id));
        } catch (err) {
            console.error('Failed to delete alert');
        }
    };

    const activeAlerts = alerts.filter(a => a.active && !a.triggered);
    const triggeredAlerts = alerts.filter(a => a.triggered);

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Bell className="w-6 h-6 text-yellow-400" />
                    <div>
                        <h2 className="text-lg font-bold">Price Alerts</h2>
                        <p className="text-xs text-text-muted">{activeAlerts.length} active alert{activeAlerts.length !== 1 ? 's' : ''}</p>
                    </div>
                </div>
                <button onClick={() => setShowForm(!showForm)}
                    className="flex items-center gap-1 px-3 py-2 bg-accent-purple text-white rounded-lg text-sm font-medium hover:bg-accent-purple/80">
                    <Plus className="w-4 h-4" /> New Alert
                </button>
            </div>

            {success && (
                <div className="flex items-center gap-2 mb-4 p-2 bg-accent-green/10 border border-accent-green/30 rounded-lg text-accent-green text-sm">
                    <Check className="w-4 h-4" /> {success}
                </div>
            )}

            {/* Create Form */}
            {showForm && (
                <form onSubmit={createAlert} className="mb-4 p-4 bg-bg-tertiary rounded-lg border border-border-color">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="block text-xs text-text-muted mb-1">Symbol</label>
                            <input type="text" value={form.symbol}
                                onChange={e => setForm(f => ({ ...f, symbol: e.target.value.toUpperCase() }))}
                                placeholder="AAPL" maxLength={10}
                                className="w-full bg-bg-card border border-border-color rounded-lg px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs text-text-muted mb-1">Condition</label>
                            <select value={form.condition}
                                onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}
                                className="w-full bg-bg-card border border-border-color rounded-lg px-3 py-2 text-sm">
                                <option value="above">Price goes above</option>
                                <option value="below">Price goes below</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="block text-xs text-text-muted mb-1">Target Price ($)</label>
                            <input type="number" step="0.01" value={form.targetPrice}
                                onChange={e => setForm(f => ({ ...f, targetPrice: e.target.value }))}
                                placeholder="150.00"
                                className="w-full bg-bg-card border border-border-color rounded-lg px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs text-text-muted mb-1">Note (optional)</label>
                            <input type="text" value={form.note}
                                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                                placeholder="Breakout level" maxLength={200}
                                className="w-full bg-bg-card border border-border-color rounded-lg px-3 py-2 text-sm" />
                        </div>
                    </div>
                    {error && (
                        <div className="flex items-center gap-1 mb-3 text-xs text-accent-red">
                            <AlertTriangle className="w-3 h-3" /> {error}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button type="submit" className="px-4 py-2 bg-accent-green text-black rounded-lg text-sm font-medium">
                            Create Alert
                        </button>
                        <button type="button" onClick={() => { setShowForm(false); setError(null); }}
                            className="px-4 py-2 bg-bg-card border border-border-color rounded-lg text-sm">
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {/* Active Alerts */}
            {loading ? (
                <div className="text-center text-text-muted py-4 text-sm">Loading alerts...</div>
            ) : activeAlerts.length === 0 ? (
                <div className="text-center text-text-muted py-6 text-sm">
                    No active alerts. Create one to get notified when a stock hits your target price.
                </div>
            ) : (
                <div className="space-y-2">
                    {activeAlerts.map(alert => (
                        <div key={alert._id} className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg border border-border-color/30">
                            <div className="flex items-center gap-3">
                                <div className={`p-1.5 rounded-full ${alert.condition === 'above' ? 'bg-accent-green/20 text-accent-green' : 'bg-accent-red/20 text-accent-red'}`}>
                                    {alert.condition === 'above' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </div>
                                <div>
                                    <div className="font-medium text-sm">
                                        {alert.symbol} <span className="text-text-muted">{alert.condition}</span> ${parseFloat(alert.targetPrice).toFixed(2)}
                                    </div>
                                    {alert.note && <div className="text-xs text-text-muted">{alert.note}</div>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-text-muted">
                                    {new Date(alert.createdAt).toLocaleDateString()}
                                </span>
                                <button onClick={() => deleteAlert(alert._id)}
                                    className="p-1 text-text-muted hover:text-accent-red transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Triggered History */}
            {triggeredAlerts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border-color">
                    <button onClick={() => setShowTriggered(!showTriggered)}
                        className="flex items-center gap-2 text-sm text-text-muted hover:text-text-secondary">
                        <Clock className="w-4 h-4" />
                        {triggeredAlerts.length} triggered alert{triggeredAlerts.length !== 1 ? 's' : ''}
                    </button>
                    {showTriggered && (
                        <div className="mt-2 space-y-1">
                            {triggeredAlerts.map(alert => (
                                <div key={alert._id} className="flex items-center justify-between p-2 bg-bg-tertiary/50 rounded-lg text-sm opacity-60">
                                    <span>
                                        {alert.symbol} {alert.condition} ${parseFloat(alert.targetPrice).toFixed(2)}
                                        → hit ${parseFloat(alert.triggeredPrice).toFixed(2)}
                                    </span>
                                    <span className="text-xs text-text-muted">
                                        {new Date(alert.triggeredAt).toLocaleString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="mt-4 text-xs text-text-muted">
                Alerts are checked every bot cycle (~2 min) against positions held. Discord notifications required.
            </div>
        </div>
    );
}
